import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext({
  user: null,
  agency: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
  refreshAgency: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = pas connecté | object = connecté
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAgency = useCallback(async () => {
    try {
      const a = await api.getAgency();
      setAgency(a);
    } catch {
      setAgency(null);
    }
  }, []);

  const bootstrap = useCallback(async () => {
    setLoading(true);
    try {
      const me = await api.me();
      setUser(me);
      await fetchAgency();
    } catch {
      setUser(null);
      setAgency(null);
    } finally {
      setLoading(false);
    }
  }, [fetchAgency]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  // Écoute l'event global émis par lib/api.js quand un 401 non récupérable arrive.
  // Nettoie l'état d'auth pour que les pages puissent rediriger vers /login.
  useEffect(() => {
    const handler = () => {
      setUser(null);
      setAgency(null);
    };
    window.addEventListener("wyvio:auth-expired", handler);
    return () => window.removeEventListener("wyvio:auth-expired", handler);
  }, []);

  const login = async (email, password) => {
    const u = await api.login(email, password);
    setUser(u);
    await fetchAgency();
    return u;
  };

  const logout = async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
      setAgency(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, agency, loading, login, logout,
               refreshAgency: fetchAgency, refreshMe: bootstrap }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
